import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  Fieldset,
  Heading,
  Paragraph,
  Switch,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { NotificationPreferences } from '../../../api/safetyRepWork';
import { useSaveNotificationPreferencesMutation } from '../../../hooks/queries/safetyRep/useSafetyRepWork';
import { togglePreference } from '../safetyRepWorkView';
import layout from '../../HRDashboard/hmsPage.module.css';

/** Each choice is saved when it is changed */
export const PreferencesCard = ({
  preferences,
}: {
  preferences: NotificationPreferences;
}): ReactElement => {
  const { t } = useTranslation();
  const [choices, setChoices] = useState(preferences);
  const save = useSaveNotificationPreferencesMutation();
  const change = (next: NotificationPreferences) => {
    setChoices(next);
    save.mutate(next);
  };

  return (
    <section
      aria-labelledby="vo-preferences"
      className={layout.sideCard}
      data-gap="sm"
    >
      <div className={layout.cardHeading}>
        <Heading level={2} data-size="xs" id="vo-preferences">
          {t('safetyRep.work.preferences.title')}
        </Heading>
        <Paragraph data-size="sm" className={layout.subtle}>
          {t('safetyRep.work.preferences.intro')}
        </Paragraph>
      </div>
      <Fieldset data-size="sm">
        <Fieldset.Legend className="ds-sr-only">
          {t('safetyRep.work.preferences.title')}
        </Fieldset.Legend>
        {choices.types.map((choice) => (
          <Switch
            key={choice.type}
            label={t(`safetyRep.work.preferences.types.${choice.type}`)}
            checked={choice.enabled}
            onChange={() => change(togglePreference(choices, choice.type))}
          />
        ))}
      </Fieldset>
      <Paragraph data-size="xs" className={layout.subtle}>
        {t('safetyRep.work.preferences.always')}
      </Paragraph>
      <Fieldset data-size="sm">
        <Fieldset.Legend>
          {t('safetyRep.work.preferences.channels')}
        </Fieldset.Legend>
        <Fieldset.Description>
          {t('safetyRep.work.preferences.inApp')}
        </Fieldset.Description>
        <Checkbox
          label={t('safetyRep.work.preferences.email')}
          checked={choices.email}
          onChange={() => change({ ...choices, email: !choices.email })}
        />
      </Fieldset>
      {save.isSuccess && (
        <Paragraph data-size="xs" role="status">
          {t('safetyRep.work.preferences.saved')}
        </Paragraph>
      )}
      {save.isError && (
        <ValidationMessage data-size="sm">
          {t('safetyRep.work.preferences.failed')}
        </ValidationMessage>
      )}
    </section>
  );
};
